import { fetchClientGraphQL } from '~/hooks/graphql.js';

const customerBillingNode = `
  billing {
    address1
    address2
    city
    country
    email
    company
    firstName
    lastName
    state
    postcode
    phone
    isEuCountry
  }
`;

const customerAvailablePaymentMethodsNode = `
  availablePaymentMethods {
    id
    isDefault
    tokenId
    type
    gateway {
      description
      icon
      id
      title
    }
    ... on PaymentTokenCC {
      cardType
      expiryMonth
      expiryYear
      id
      isDefault
      last4
      tokenId
      type
    }
  }
`;

const getCustomerNode = `
  customer {
    ${ customerBillingNode }
    vatNumber
    isVatExempt
    ${ customerAvailablePaymentMethodsNode }
  }
`;

const getViewerNode = `
  viewer {
    capKey
    databaseId
    email
    firstName
    id
    lastName
    locale
    name
    nicename
    nickname
    registeredDate
    shouldShowAdminToolbar
    slug
    uri
    url
    userId
    username
    wooSessionToken
    roles {
      nodes {
        name
        id
      }
    }
    nonce
  }
`;

const getSubscriptionsNode = `
  subscriptions {
    availableActions {
      action
      url
    }
    id
    databaseId
    date
    endDate
    nextPaymentDate
    hasDownloadableItem
    needsPayment
    modified
    status
    lineItems {
      nodes {
        product {
          node {
            name
            link
            databaseId
            date
            dateGmt
            dateOnSaleFrom
            dateOnSaleTo
            description(format: RENDERED)
            featuredImageDatabaseId
            image {
              databaseId
              guid
              mediaItemUrl
              id
              fileSize(size: MEDIUM)
              status
            }
            uri
            title(format: RENDERED)
            status
            slug
            sku
            guid
            id
          }
        }
        databaseId
        quantity
        subtotal
        subtotalTax
        total
        variationId
        productId
        id
      }
    }
    downloadableItems {
      nodes {
        download {
          downloadId
          file
          fileExists
          fileExt
          filePathType
          fileType
          name
        }
        accessExpires
        downloadId
        downloadsRemaining
        name
        product {
          databaseId
          featured
          name
          sku
          id
        }
        url
        id
      }
    }
    billing {
      address1
      address2
      city
      company
      country
      email
      firstName
      lastName
      phone
      postcode
      state
    }
    billingInterval
    billingPeriod
    canRenewEarly
    cancelledDate
    createdVia
    currency
    dateCompleted
    datePaid
    orderKey
    orderNumber
  }
`

/**
 * If no orderId passed, all product download permission details for user are returned
 */
const userDownloadableProductsQuery = `
query UserDownloadableProducts(
  $orderId: String,
  $refresh: Boolean
) {
  userDownloadableProducts(orderId: $orderId, refresh: $refresh) {
    totalCount
    nodes {
      productId
      productName
      productSlug
      orderId
      downloadsRemaining
      downloadExpiry
      totalDownloadFiles
      downloadFiles {
        downloadExpiry
        downloadId
        downloadLink
        downloadsRemaining
        fileName
      }
    }
  }
}
`

async function fetchUserDownloadableProductsData ({ orderId, refresh = false }) {
  return await fetchClientGraphQL(userDownloadableProductsQuery, { orderId, refresh }, (data) => {
    return data
  });
}

const getCurrentUserDataQuery = `query Viewer { ${ getViewerNode } }`;

const getCustomerDataQuery = `query customerData { ${ getCustomerNode } }`;

const getCustomerSubscriptionDataQuery = `
  query Customer {
    customer {
      displayName
      databaseId
      date
      email
      id
      ${ getSubscriptionsNode }
    }
  }
`;

/**
 * fetchUserData
 * name, role, email
 */
const fetchUserData = () => {
  return fetchClientGraphQL(getCurrentUserDataQuery, {}, (data) => {
    return data
  });
};

/**
 * fetchCustomerData
 * billing address, stored payment methods
 */
async function fetchCustomerData () {
  return await fetchClientGraphQL(getCustomerDataQuery, {}, (data) => {

    if (data?.customer?.availablePaymentMethods) {
      // filter to return only stored card type 'CC'
      data.customer.availablePaymentMethods = data.customer.availablePaymentMethods.filter((storedMethod) => storedMethod.type === 'CC')
    }

    return data
  });
}

async function fetchCustomerSubscriptionData () {
  return await fetchClientGraphQL(getCustomerSubscriptionDataQuery, {}, (data) => {
    return data
  });
}

export {
  getViewerNode,
  getCurrentUserDataQuery,
  fetchCustomerData,
  fetchCustomerSubscriptionData,
  fetchUserDownloadableProductsData,
  fetchUserData,
}
